import { auth } from '../firebase'; // Import Firebase auth instance

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

// A job completed within this many days of another job on the same schedule, for the same
// household and an overlapping service, is treated as a possible duplicate.
const DUPLICATE_COMPLETION_WINDOW_DAYS = 7;

// A visit one full cycle after the last one is the schedule working, not a duplicate. On an
// "Every 7 Days" schedule every visit is exactly 7 days apart, so the flat window flagged all of
// them. Only ever narrows the window for short cycles, never widens it.
const duplicateWindowDays = (frequencyType, frequencyValue) => {
  const unit = String(frequencyType || '').toLowerCase();
  const cycleDays = unit === 'days' ? Number(frequencyValue)
    : unit === 'weeks' ? Number(frequencyValue) * 7
      : null;
  return cycleDays > 0
    ? Math.min(DUPLICATE_COMPLETION_WINDOW_DAYS, cycleDays - 1)
    : DUPLICATE_COMPLETION_WINDOW_DAYS;
};

// Statuses that count as "already done" for duplicate purposes.
const DUPLICATE_COMPLETION_STATUSES = ['completed', 'invoiced'];

// Jobs store services as { serviceTypeId, name }; 'code' is not populated on jobs, so
// serviceTypeId is the primary key with code/name as fallbacks.
const getServiceKey = (service) => service?.serviceTypeId || service?.code || service?.name;

const toJsDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return new Date(value);
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value._seconds === 'number') return new Date(value._seconds * 1000);
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  return null;
};

/**
 * Finds already-completed jobs that look like duplicates of the one being completed: same
 * schedule, same household, an overlapping service, and a completion date within a week.
 *
 * A port of the franchisee app's check (jims-mowing-app jobService.checkNearbyCompletedJobs) so
 * an admin completing a job sees the same warning. Advisory only — it returns [] on any failure
 * rather than throwing, so it can never block a completion.
 *
 * Scoped by schedule rather than by household: it is the only indexed query shape, and since a
 * duplicate must be same-schedule anyway it is also the exact set we want.
 *
 * @param {object} options
 * @param {string} options.jobId - The job being completed; excluded from its own results.
 * @param {string} options.fseId - Franchisee the job belongs to.
 * @param {string} options.householdId - Household (UI "Client") the job is for.
 * @param {string} options.scheduleId - Required: only jobs confirmed on this same schedule can be
 *   duplicates. A job without one is never flagged, and never flags anything.
 * @param {Array} options.services - Services on the job being completed.
 * @param {string} options.proposedCompletionDate - Completion date being set (YYYY-MM-DD).
 * @returns {Promise<Array>} Matching jobs, most recent first; [] when there are none.
 */
export const checkNearbyCompletedJobs = async ({
  jobId, fseId, householdId, scheduleId, services, proposedCompletionDate,
  frequencyType, frequencyValue,
}) => {
  // No scheduleId means no candidate can ever be confirmed same-schedule, so skip the fetches.
  if (!fseId || !householdId || !scheduleId || !services?.length || !proposedCompletionDate) return [];

  const proposedDate = new Date(proposedCompletionDate);
  if (isNaN(proposedDate.getTime())) return [];

  const currentServiceKeys = services.map(getServiceKey).filter(Boolean);
  if (currentServiceKeys.length === 0) return [];

  try {
    // Fetched by schedule, then filtered here. Querying by fseId + householdId + status ordered on
    // completionDate has no composite index, and listJobs turns that FAILED_PRECONDITION into a
    // 'fail' response — which this function would read as "no duplicates" and warn about nothing.
    // A schedule holds tens of jobs, so pulling it whole and filtering locally is cheap and exact.
    const scheduleJobs = await getJobsByScheduleId(scheduleId, 500);

    return scheduleJobs.filter((job) => {
      if (job.id === jobId) return false;

      // Only jobs already finished can be duplicated.
      if (!DUPLICATE_COMPLETION_STATUSES.includes(job.status)) return false;

      // Belt and braces: the schedule already implies both, but a job can in principle be
      // reassigned, and a duplicate must be the same franchisee at the same address.
      if (job.fseId !== fseId || job.householdId !== householdId) return false;

      const jobServiceKeys = (job.services || []).map(getServiceKey).filter(Boolean);
      if (!currentServiceKeys.some((key) => jobServiceKeys.includes(key))) return false;

      const jobCompletionDate = toJsDate(job.completionDate);
      if (!jobCompletionDate || isNaN(jobCompletionDate.getTime())) return false;

      const daysDiff = Math.abs(proposedDate.getTime() - jobCompletionDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysDiff <= duplicateWindowDays(frequencyType, frequencyValue);
    });
  } catch (error) {
    console.error('[checkNearbyCompletedJobs] Duplicate check failed; completing without a warning:', error);
    return [];
  }
};

/**
 * Fetches a list of jobs from the backend.
 * @param {object} [options={}] - Optional parameters for filtering and pagination.
 * @param {string} [options.fseId] - Filter by franchisee ID.
 * @param {string} [options.clientId] - Filter by Supplier (original Client) ID.
 * @param {string} [options.householdId] - Filter by Client (formerly Household) ID. // Updated comment
 * @param {string} [options.status] - Filter by job status.
 * @param {number} [options.pageSize=10] - The number of jobs to fetch per page.
 * @param {string} [options.startAfterDocId] - The document ID to start fetching after.
 * @param {string} [options.orderBy='jobDate'] - Field to order by.
 * @param {'asc'|'desc'} [options.orderDirection='asc'] - Order direction.
 * @returns {Promise<object>} - The response data containing jobs and pagination info.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const listJobs = async (options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const {
    fseId = null,
    clientId = null, // Supplier ID
    householdId = null, // Client (formerly Household) ID
    status = null,
    pageSize = 10,
    startAfterDocId = null,
    orderBy = 'jobDate',
    orderDirection = 'asc',
  } = options;

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/job/list`;

    // Build the body object, always include userType and pagination/ordering
    const body = {
        userType: "admin", // Mandatory field
        pageSize,
        orderBy,
        orderDirection
    };
    if (fseId) body.fseId = fseId;
    if (clientId) body.clientId = clientId; // Pass Supplier ID
    if (householdId) body.householdId = householdId; // Pass Client ID (as householdId)
    if (status) body.status = status;
    if (startAfterDocId) body.startAfterDocId = startAfterDocId;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to list jobs');
    }
    // Ensure the response structure matches expectations
    return responseData.data || { jobs: [], nextPageDocId: null };

  } catch (error) {
    console.error("Error listing jobs:", error);
    throw error; // Re-throw the error
  }
};

/**
 * Fetches the details of a specific job.
 * @param {string} jobId - The ID of the job to fetch.
 * @returns {Promise<object>} - The job data.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const getJobDetails = async (jobId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!jobId) {
    throw new Error("Job ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/job/details`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ jobId }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to get job details');
    }

    return responseData.data; // Return the job data object

  } catch (error) {
    console.error(`Error fetching job details for ${jobId}:`, error);
    throw error;
  }
};

/**
 * Updates specific fields of a job by an admin.
 * @param {string} jobId - The ID of the job to update.
 * @param {object} updateData - An object containing the fields to update.
 * @returns {Promise<object>} - The updated job data.
 * @throws {Error} - Throws an error if the update fails or the user is not authenticated.
 */
export const adminUpdateJob = async (jobId, updateData) => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error("User not authenticated.");
    }
    if (!jobId || !updateData || Object.keys(updateData).length === 0) {
        throw new Error("Job ID and update data are required.");
    }

    let response;

    try {
        const idToken = await user.getIdToken();
        const url = `${BASE_URL}/job/admin-update`;

        // Create a shallow copy of updateData to modify
        const cleanedUpdateData = { ...updateData };

        // Remove properties with null values from the update data
        for (const key in cleanedUpdateData) {
            if (cleanedUpdateData[key] === null) {
                delete cleanedUpdateData[key];
            }
        }

        const payload = {
            jobId: jobId,
            ...cleanedUpdateData, // Spread the cleaned fields
            userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        console.log("Sending admin update payload:", JSON.stringify(payload, null, 2));

        response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const responseData = await response.json();

        if (!response.ok) {
            // Carry the status and body so callers can react to specific failures, e.g. the
            // 409 monthly-allocation breach that offers the admin an override.
            const error = new Error(responseData.message || `HTTP error! status: ${response.status}`);
            error.response = { status: response.status, data: responseData };
            throw error;
        }

        if (responseData.status !== 'success') {
            throw new Error(responseData.message || 'Failed to update job via admin');
        }

        return responseData; // Return full response (includes invoiceRctiResults, xeroReconnectRequired, xeroSyncWarnings)

    } catch (error) {
        console.error("Error updating job via admin:", error);
        if (error instanceof SyntaxError && response) {
            try {
                const rawText = await response.text();
                console.error("Raw response text (likely HTML):", rawText);
            } catch (textError) {
                console.error("Could not get raw response text:", textError);
            }
        }
        throw error;
    }
};

/**
 * Updates the status of a specific job.
 * @param {string} jobId - The ID of the job to update.
 * @param {string} status - The new status value (e.g., 'pending-approval').
 * @returns {Promise<object>} - The response data containing the updated job.
 * @throws {Error} - Throws an error if the update fails or the user is not authenticated.
 */
export const updateJobStatus = async (jobId, status) => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated.");
    }
    if (!jobId || !status) {
        throw new Error("Job ID and new status are required.");
    }

    let response; // Define response outside try block to access in catch

    try {
        const idToken = await user.getIdToken();
        const url = `${BASE_URL}/job/update`; // Endpoint for updating

        // --- Corrected payload structure ---
        const payload = {
            jobId: jobId,
            status: status, // Status field directly in the payload
            userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        // --- End correction ---

        // --- Log the payload before sending ---
        console.log("Sending update payload:", JSON.stringify(payload, null, 2));
        // --- End log ---

        response = await fetch(url, { // Assign to outer response variable
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        // Try to parse JSON first
        const responseData = await response.json();

        if (!response.ok) {
            throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
        }

        if (responseData.status !== 'success') {
            throw new Error(responseData.message || 'Failed to update job status');
        }

        return responseData.data; // Return the updated job data

    } catch (error) {
        console.error("Error updating job status:", error);

        // --- Add this block to log raw response on JSON parse error ---
        if (error instanceof SyntaxError && response) {
            // If it was a JSON parsing error, try to get the raw text
            try {
                const rawText = await response.text();
                console.error("Raw response text (likely HTML):", rawText);
            } catch (textError) {
                console.error("Could not get raw response text:", textError);
            }
        }
        // --- End of added block ---

        throw error; // Re-throw the original error
    }
};

/**
 * Fetches all jobs for a specific schedule.
 *
 * The query shape here (scheduleId + orderBy jobDate ASC, no fseId/status/householdId filters) is
 * the one Firestore has a composite index for. Adding filters or flipping the direction throws
 * FAILED_PRECONDITION, which listJobs swallows into a 'fail' response — so filter client-side
 * rather than pushing more constraints into the request.
 *
 * @param {string} scheduleId - The schedule ID.
 * @param {number} [pageSize=100] - Max jobs to fetch. Ordered oldest-first, so raising this is how
 *   callers that care about recent jobs avoid truncation.
 * @returns {Promise<Array>} - Array of jobs.
 * @throws {Error} - Throws an error if request fails.
 */
export const getJobsByScheduleId = async (scheduleId, pageSize = 100) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!scheduleId) {
    throw new Error("Schedule ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const requestBody = {
      userType: 'admin',
      scheduleId: scheduleId,
      pageSize,
      orderBy: 'jobDate',
      orderDirection: 'asc'
    };
    
    console.log('[getJobsByScheduleId] Request body:', requestBody);
    
    const response = await fetch(`${BASE_URL}/job/list`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();
    console.log('[getJobsByScheduleId] Response:', data);

    if (!response.ok || data.status !== 'success') {
      throw new Error(data.message || 'Failed to fetch jobs');
    }

    const jobs = data.data?.jobs || [];
    console.log(`[getJobsByScheduleId] Returning ${jobs.length} jobs for schedule ${scheduleId}`);
    return jobs;
  } catch (error) {
    console.error("Error fetching jobs by schedule ID:", error);
    throw error;
  }
};

/**
 * Generate invoices for all completed jobs in a monthly allocation.
 * @param {string} scheduleId - The schedule ID.
 * @param {string} monthYear - The month and year (e.g., "November 2025").
 * @returns {Promise<object>} - Response with invoice generation results.
 * @throws {Error} - Throws an error if request fails.
 */
export const generateMonthlyAllocationInvoice = async (scheduleId, monthYear) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/job/generate-monthly-invoice`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ scheduleId, monthYear }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to generate monthly allocation invoice');
    }

    return responseData;
  } catch (error) {
    console.error("Error generating monthly allocation invoice:", error);
    throw error;
  }
};

/**
 * Create invoice and RCTI for a completed job (manual override).
 * This bypasses the automatic creation checks and allows manual creation for completed jobs.
 * 
 * @param {string} jobId - The ID of the job to create invoice/RCTI for.
 * @returns {Promise<Object>} - Response with status (success, partial, fail) and details.
 * @throws {Error} - Throws an error if request fails.
 */
export const createInvoiceAndRcti = async (jobId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/job/create-invoice-rcti`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ jobId }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData;
  } catch (error) {
    console.error("Error creating invoice and RCTI:", error);
    throw error;
  }
};

/**
 * Regenerate a completed job's draft invoice/RCTI after a price edit.
 * dryRun returns old→new previews for both documents (feeds the confirmation
 * modal); executing regenerates the selected documents, reusing the number with
 * a "(n)" sequence suffix (e.g. LP-RCTI-123 -> LP-RCTI-123(1)).
 *
 * @param {string} jobId - The ID of the completed job.
 * @param {{regenerateInvoice?: boolean, regenerateRcti?: boolean, dryRun?: boolean}} options
 * @returns {Promise<Object>} - { status, dryRun?, invoice, rcti }
 * @throws {Error} - Throws an error if request fails.
 */
export const regenerateJobDocuments = async (jobId, { regenerateInvoice = false, regenerateRcti = false, dryRun = false } = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/job/regenerate-documents`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ jobId, regenerateInvoice, regenerateRcti, dryRun }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData;
  } catch (error) {
    console.error("Error regenerating job documents:", error);
    throw error;
  }
};

/**
 * Fetches the audit log entries for a specific job.
 * @param {string} jobId - The ID of the job.
 * @returns {Promise<Array>} - Array of audit log entry objects.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const getJobAuditLog = async (jobId) => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated.");
  if (!jobId) throw new Error("Job ID is required.");

  try {
    const idToken = await user.getIdToken();
    const response = await fetch(`${BASE_URL}/job/audit-log`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ jobId }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to get job audit log');
    }

    return responseData.data?.entries || [];
  } catch (error) {
    console.error(`Error fetching audit log for job ${jobId}:`, error);
    throw error;
  }
};
